<template>
  <div ref="panelRef" class="page-content-drawer-panel" :class="{ 'is-flow-review': opt.flowReviewEdit }">
    <template v-if="opt.flowReviewEdit">
      <div class="flow-review-layout">
        <div class="flow-review-layout__editor">
          <XmPageContent :key="item.id" :props="pageContentProps" :p-model-obj="null" />
        </div>
        <aside class="flow-review-layout__side">
          <div class="flow-review-approve">
            <div class="opinion-input-wrap">
              <a-textarea
                v-model:value="opinion"
                class="opinion-input"
                :rows="4"
                :maxlength="100"
                placeholder="请输入附言"
              />
            </div>
            <div class="opinion-footer">
              <a-button
                size="small"
                class="return-btn"
                :loading="backLoading"
                :disabled="submitting"
                @click="onReject"
              >
                退回
              </a-button>
              <a-button
                size="small"
                type="primary"
                class="submit-btn"
                :loading="submitting"
                :disabled="backLoading"
                @click="onApprove"
              >
                审核
              </a-button>
            </div>
          </div>
          <div class="flow-review-log">
            <XmFlowLog
              :key="`flow-log-${opt.busId}`"
              :props="flowLogProps"
              :styles="{}"
              :model="{}"
              :events="[]"
              :data-config="{}"
            />
          </div>
        </aside>
      </div>
    </template>
    <template v-else>
      <div class="page-content-drawer-body">
        <XmPageContent :key="item.id" :props="pageContentProps" :p-model-obj="null" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onBeforeUnmount, provide, ref } from 'vue';
  import { Button as AButton, Textarea as ATextarea, Modal } from 'ant-design-vue';
  import XmPageContent from '@/main-page-base/ctls/containers/xmPageContent/index.vue';
  import XmFlowLog from '@/main-page-extend/processCtls/xmFlowLog/index.vue';
  import { QueryCommand } from 'xmgoing-server';
  import { StorageUtils } from 'xmgoing-utils';
  import GoingUtils from '@/utils/goingUtils';
  import { usePageModelsStore } from '@/store/modules/pageModels';
  import { useDataSourceStore } from '@/store/modules/dataSourceStore';
  import { storeToRefs } from 'pinia';
  import { ViewModel } from '@/main-page/jsParseEngine/ViewModel';

  export type FlowReviewDrawerOpt = {
    flowReviewEdit?: boolean;
    approveUrl?: string;
    busId?: string | number;
    /** 来源列表 gridKey，提交成功后 flushGrid 继承查询条件 */
    gridKey?: string;
    [key: string]: any;
  };

  export type FlowReviewDrawerItem = {
    id: string;
    pageCode: string;
    moduleCode: string;
    title: string;
    opt?: FlowReviewDrawerOpt;
  };

  const FLOW_RECORD_SESSION_KEY = 'flowRecordId';

  const props = defineProps<{
    item: FlowReviewDrawerItem;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const panelRef = ref<HTMLElement | null>(null);
  const opt = computed(() => props.item.opt || {});
  // 审核可写：注入 flowContainer，表单加载后走 listFieldByBusId 字段 ACL
  const flowContainer = ref(!!opt.value.flowReviewEdit);
  provide('flowContainer', flowContainer);
  provide('flowReviewSession', opt.value);

  const pageContentProps = computed(() => ({
    width: '100%',
    height: '100%',
    show: true,
    key: props.item.pageCode,
    pageCode: props.item.pageCode,
    moduleCode: props.item.moduleCode,
    columnConfigFlag: true,
  }));

  /** xmFlowLog 通过 session 取业务 id（与部件内 sessionFlag 约定一致） */
  const prevFlowRecordId =
    opt.value.flowReviewEdit && opt.value.busId != null && opt.value.busId !== ''
      ? StorageUtils.getSessionItem(FLOW_RECORD_SESSION_KEY)
      : null;

  if (opt.value.flowReviewEdit && opt.value.busId != null && opt.value.busId !== '') {
    StorageUtils.addSessionItem(FLOW_RECORD_SESSION_KEY, String(opt.value.busId));
  }

  onBeforeUnmount(() => {
    if (!opt.value.flowReviewEdit) return;
    if (prevFlowRecordId != null && prevFlowRecordId !== '') {
      StorageUtils.addSessionItem(FLOW_RECORD_SESSION_KEY, prevFlowRecordId);
    } else {
      StorageUtils.removeSessionItem(FLOW_RECORD_SESSION_KEY);
    }
  });

  const flowLogProps = {
    sessionFlag: true,
    titleShowFlag: true,
    showImgFlag: true,
    width: '100%',
    height: '100%',
    show: true,
  };

  const submitting = ref(false);
  const backLoading = ref(false);
  const opinion = ref('');
  const pageModelsStore: any = usePageModelsStore();
  const dataSourceStore: any = useDataSourceStore();
  const { state } = storeToRefs(pageModelsStore);

  /**
   * 与 getPageFormObj 同思路：在抽屉内找 .xmFormBody，用其 id 取表单值
   */
  const resolveDrawerForm = () => {
    const root = panelRef.value;
    if (!root) return null;
    const formEl = root.querySelector('.xmFormBody') as HTMLElement | null;
    const formId = formEl?.getAttribute('id') || '';
    if (!formId) return null;
    const formData = state.value?.[formId];
    if (formData == null) return null;
    return { formId, formData };
  };

  const getViewModel = () =>
    new ViewModel({ viewPage: {}, pageModelsStore, dataSourceStore, state });

  const resolveDocNo = (formData?: any) => {
    if (!formData || typeof formData !== 'object') return '';
    return String(formData.docNo || formData.orderNumber || formData.billNo || '').trim();
  };

  const submitApprove = async (selected: string) => {
    if (submitting.value || backLoading.value) {
      return;
    }
    const url = String(opt.value.approveUrl || '').trim();
    if (!url) {
      GoingUtils.showWarningMsg('未配置审核提交接口');
      return;
    }

    const messageText = String(opinion.value || '').trim();
    if (selected === '不同意' && !messageText) {
      GoingUtils.showWarningMsg('请输入附言');
      return;
    }

    const loadingRef = selected === '不同意' ? backLoading : submitting;
    loadingRef.value = true;

    const doPost = async (formData?: any) => {
      try {
        const formVals =
          formData && typeof formData === 'object' && !Array.isArray(formData)
            ? { ...formData }
            : {};
        const queryCommand = new QueryCommand({ url, method: 'POST', showTip: true });
        await queryCommand.executeQueryPromise({
          params: {
            ...formVals,
            id: formVals.id ?? opt.value.busId,
            commentType: selected,
            message: messageText,
          },
        });
        GoingUtils.showMessage(selected === '同意' ? '审核通过' : '退回成功');
        // 与简易流程提交成功一致：flushGrid 继承当前查询条件
        const gridKey = String(opt.value.gridKey || '').trim();
        if (gridKey) {
          try {
            getViewModel().flushGrid(gridKey, {}, true, true, 'and', undefined);
          } catch (e) {
            console.error(e);
          }
        }
        emit('close');
      } catch (e) {
        console.error(e);
      } finally {
        loadingRef.value = false;
      }
    };

    const form = resolveDrawerForm();

    if (selected === '不同意') {
      // 不同意：意见必填；表单有值则一并提交（后端按 commentType 决定是否落库）
      await doPost(form?.formData);
      return;
    }

    if (!form) {
      loadingRef.value = false;
      GoingUtils.showWarningMsg('未获取到编辑部件表单，请确认页面内存在表单');
      return;
    }
    const viewModel = getViewModel();
    viewModel.validateForm(
      form.formId,
      async () => {
        await doPost(form.formData);
      },
      () => {
        loadingRef.value = false;
        GoingUtils.showWarningMsg('请完善表单后再审核');
      },
    );
  };

  /** 退回：确认后走原「不同意 + 确定」同一套提交 */
  const onReject = () => {
    if (submitting.value || backLoading.value) {
      return;
    }
    const messageText = String(opinion.value || '').trim();
    if (!messageText) {
      GoingUtils.showWarningMsg('请输入附言');
      return;
    }
    const docNo = resolveDocNo(resolveDrawerForm()?.formData);
    Modal.confirm({
      title: '退回确认',
      content: docNo ? `确定要退回单据 ${docNo} 吗？` : '确定要退回该单据吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => submitApprove('不同意'),
    });
  };
  /** 审核：与原「同意 + 确定」同一套提交 */
  const onApprove = () => submitApprove('同意');
</script>

<style scoped>
  .page-content-drawer-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .page-content-drawer-panel.is-flow-review {
    height: 100%;
  }

  .page-content-drawer-body {
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px 16px;
    box-sizing: border-box;
  }

  .page-content-drawer-body :deep(.xmPageContent),
  .flow-review-layout__editor :deep(.xmPageContent) {
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 100%;
    box-sizing: border-box;
  }

  /* 用 grid 固定右侧栏，避免左侧表单撑开把审批区推出视口 */
  .flow-review-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    box-sizing: border-box;
  }

  .flow-review-layout__editor {
    min-width: 0;
    min-height: 0;
    overflow: auto;
    padding: 12px 16px;
    box-sizing: border-box;
  }

  .flow-review-layout__side {
    width: 360px;
    max-width: 360px;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-left: 1px solid #f0f0f0;
    background: #fbfafb;
    box-sizing: border-box;
  }

  .flow-review-approve {
    flex-shrink: 0;
    padding: 14px 12px 12px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
    box-sizing: border-box;
  }

  .opinion-input-wrap {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    transition: border-color 0.3s;
  }

  .opinion-input-wrap:focus-within {
    border-color: var(--ant-primary-color, #0a6cd5);
  }

  .opinion-input-wrap :deep(.ant-input) {
    border: none;
    resize: none;
    box-shadow: none;
  }

  .opinion-input-wrap :deep(.ant-input:focus) {
    box-shadow: none;
  }

  .opinion-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
  }

  .return-btn {
    color: #ff4d4f;
    border-color: #ff4d4f;
    background-color: #fff;
  }

  .return-btn:hover,
  .return-btn:focus {
    color: #ff4d4f;
    border-color: #ff4d4f;
    background-color: #fff1f0;
  }

  .submit-btn {
    background-color: var(--ant-primary-color, #0a6cd5);
    border-color: var(--ant-primary-color, #0a6cd5);
  }

  .submit-btn:hover,
  .submit-btn:focus {
    background-color: #3b8ee0;
    border-color: #3b8ee0;
  }

  .flow-review-log {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .flow-review-log :deep(.xmFlowLog) {
    height: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
</style>
